<script>
  // A 24-hour time picker (00:00 to 23:59) that looks the same in every browser and ignores the computer's 12/24-hour setting.
  // Value is "HH:MM". Choose an hour and a minute in the list, or type the time.
  import { inputCls } from '../../lib/ui.js';
  import Clock from 'lucide-svelte/icons/clock';

  let { value = $bindable('09:00'), id = '', disabled = false, step = 5, 'aria-label': ariaLabel = undefined } = $props();

  const pad = (n) => String(n).padStart(2, '0');
  const valid = (s) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s || '');
  const hours = Array.from({ length: 24 }, (_, i) => pad(i));
  const hh = $derived(valid(value) ? value.slice(0, 2) : '');
  const mm = $derived(valid(value) ? value.slice(3, 5) : '');
  const minutes = $derived.by(() => {
    const list = Array.from({ length: Math.ceil(60 / step) }, (_, i) => pad(i * step));
    return mm && !list.includes(mm) ? [...list, mm].sort() : list;
  });

  let open = $state(false);
  let root;
  let typed = $state('');
  $effect(() => { typed = value || ''; });
  function setH(h) { value = `${h}:${mm || '00'}`; scrollTo(); }
  function setM(m) { value = `${hh || '09'}:${m}`; open = false; }
  // Accepts 930, 9:30 or 09:30 and settles on HH:MM.
  function commit() {
    const m = /^(\d{1,2})[:.]?(\d{2})$/.exec(typed.trim());
    if (m && +m[1] < 24 && +m[2] < 60) value = `${pad(+m[1])}:${m[2]}`;
    else typed = value || '';
  }
  function scrollTo() {
    queueMicrotask(() => root?.querySelectorAll('[data-sel="true"]').forEach(el => el.scrollIntoView({ block: 'center' })));
  }
</script>

<svelte:window onpointerdown={(e) => { if (open && root && !root.contains(e.target)) open = false; }} onkeydown={(e) => { if (open && e.key === 'Escape') { e.stopPropagation(); open = false; } }} />

<div class="relative" bind:this={root}>
  <div class="relative">
    <input {id} {disabled} inputmode="numeric" autocomplete="off" maxlength="5" placeholder="HH:MM" aria-label={ariaLabel} class="{inputCls} pr-9 tabular-nums"
      bind:value={typed} onfocus={() => { if (!disabled) { open = true; scrollTo(); } }} onblur={commit} onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); open = false; } }} />
    <button type="button" tabindex="-1" {disabled} aria-label="Choose a time" class="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-50" onclick={() => { open = !open; if (open) scrollTo(); }}><Clock size={15} /></button>
  </div>
  {#if open}
    <div role="dialog" aria-label="Choose a time" class="absolute left-0 z-[60] mt-1 flex w-44 gap-1 rounded-lg border border-border bg-background p-2 shadow-lg">
      <div class="max-h-52 flex-1 overflow-y-auto" role="listbox" aria-label="Hour">
        {#each hours as h}<button type="button" role="option" aria-selected={h === hh} data-sel={h === hh} onclick={() => setH(h)} class="block h-8 w-full rounded-md text-sm tabular-nums transition-colors {h === hh ? 'bg-foreground text-background' : 'hover:bg-accent'}">{h}</button>{/each}
      </div>
      <div class="max-h-52 flex-1 overflow-y-auto" role="listbox" aria-label="Minute">
        {#each minutes as m}<button type="button" role="option" aria-selected={m === mm} data-sel={m === mm} onclick={() => setM(m)} class="block h-8 w-full rounded-md text-sm tabular-nums transition-colors {m === mm ? 'bg-foreground text-background' : 'hover:bg-accent'}">{m}</button>{/each}
      </div>
    </div>
  {/if}
</div>
